import { omit } from 'lodash';
import { AnyStateMachine } from 'xstate';
import { ParsedQueryParams } from '~/providers/query-params/types';

/*
  TLDR: This allows parent machines to safely pass context into child machines.

  Why?: When you pass data to an invoked child machine via the "data" prop,
  it completely overwrites the initial machine context rather than merging.
  This is supposed to be fixed in v5, but for now, this util does that -
  merges initial context w/ overrides that can read from the parentMachine context.

  **Simplest usage:**
  const userContext = contextCreator(userMachine);
  userContext(parentContext => ({ zipCode: parentContext.zipCode }));

  **Requiring certain context properties to always be passed in:**
  const userContext = contextCreator<UserContext, 'utility' | 'zipCode>(userMachine);
  userContext(parentContext => ({ zipCode: parentContext.zipCode }));
  // will throw a TS error saying utility is required

  **Adding a query params picker:**
  const userContext = contextCreator<UserContext, 'utility' | 'zipCode>(userMachine, queryParams => ({
    firstName: queryParams.firstName,
    lastName: queryParams.lastName,
  }));
  userContext(parentContext => ({
    queryParams: parentContext.queryParams,
    utility: parentContext.utilitySelection,
    zipCode: parentContext.zipCode,
  }))
  // pulls first and last name from the query params and puts them in the returned context object
  // completely opt-in - if you don't pass queryParams in, it won't do anything
*/

// TODO: seems to allow values that aren't in context
// & queryParams part could be improved
export const contextCreator =
  <TContext extends {}, TRequired extends keyof TContext = keyof {}>(
    machine: () => AnyStateMachine,
    queryParamsPicker?: (queryParams: ParsedQueryParams) => Partial<TContext>,
  ) =>
  <TFunnelContext extends { queryParams?: ParsedQueryParams }>(
    overrideFunction: (
      funnelContext: TFunnelContext,
    ) => Partial<TContext> &
      Required<Pick<TContext, TRequired>> & { queryParams?: ParsedQueryParams },
  ) =>
  (funnelContext: TFunnelContext) => ({
    ...machine().initialState.context,
    ...omit(overrideFunction(funnelContext), ['queryParams']),
    ...(queryParamsPicker && funnelContext?.queryParams
      ? queryParamsPicker(funnelContext.queryParams)
      : undefined),
  });
